import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Search,
  Image as ImageIcon,
  Copy,
  Check,
  X,
  ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Asset {
  filename: string;
  path: string;
  url: string;
  category: string;
  size_mb: number;
  type: string;
  subfolder?: string;
}

interface CategoryData {
  name: string;
  count: number;
  assets: Asset[];
}

interface AssetResponse {
  success: boolean;
  total_count: number;
  categories: Record<string, CategoryData>;
}

const AssetManagement: React.FC = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Record<string, CategoryData>>({});
  const [filteredAssets, setFilteredAssets] = useState<Record<string, CategoryData>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      navigate('/admin/login');
      return;
    }
    fetchAssets();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, searchTerm, selectedCategory]);

  const fetchAssets = async () => {
    try {
      const response = await fetch('/api/admin/assets/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data: AssetResponse = await response.json();
        setAssets(data.categories);
        setFilteredAssets(data.categories);
      } else {
        toast.error('Failed to fetch assets');
      }
    } catch (error) {
      toast.error('Error loading assets');
    } finally {
      setLoading(false);
    }
  };

  const filterAssets = () => {
    if (selectedCategory === 'all' && !searchTerm) {
      setFilteredAssets(assets);
      return;
    }

    const filtered: Record<string, CategoryData> = {};

    Object.entries(assets).forEach(([key, data]) => {
      // Filter by category
      if (selectedCategory !== 'all' && key !== selectedCategory) {
        return;
      }

      // Filter by search term
      let categoryAssets = data.assets;
      if (searchTerm) {
        categoryAssets = categoryAssets.filter(asset =>
          asset.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.type.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (categoryAssets.length > 0) {
        filtered[key] = {
          ...data,
          assets: categoryAssets,
          count: categoryAssets.length
        };
      }
    });

    setFilteredAssets(filtered);
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const downloadAsset = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const getTotalAssetCount = () => {
    return Object.values(filteredAssets).reduce((sum, cat) => sum + cat.count, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="text-white text-xl">Loading assets...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/blog')}
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">Marketing Assets</h1>
              <p className="text-blue-100 mt-1">
                {getTotalAssetCount()} assets available for download
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories ({getTotalAssetCount()})</option>
              {Object.entries(assets).map(([key, data]) => (
                <option key={key} value={key}>
                  {data.name} ({data.count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Asset Grid by Category */}
        {Object.entries(filteredAssets).map(([categoryKey, categoryData]) => (
          <div key={categoryKey} className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              {categoryData.name} ({categoryData.count})
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categoryData.assets.map((asset) => (
                <div
                  key={asset.url}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  {/* Image Preview */}
                  <div
                    className="aspect-square bg-gray-100 flex items-center justify-center cursor-pointer overflow-hidden"
                    onClick={() => setPreviewAsset(asset)}
                  >
                    {asset.type === 'SVG' ? (
                      <div className="flex items-center justify-center p-4">
                        <ImageIcon className="h-16 w-16 text-gray-400" />
                        <span className="ml-2 text-sm text-gray-500">SVG</span>
                      </div>
                    ) : (
                      <img
                        src={asset.url}
                        alt={asset.filename}
                        className="w-full h-full object-contain hover:scale-110 transition-transform"
                      />
                    )}
                  </div>

                  {/* Asset Info */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate" title={asset.filename}>
                      {asset.filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {asset.type} • {asset.size_mb} MB
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => copyUrl(asset.url)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        {copiedUrl === asset.url ? (
                          <Check className="h-3 w-3 mr-1" />
                        ) : (
                          <Copy className="h-3 w-3 mr-1" />
                        )}
                        {copiedUrl === asset.url ? 'Copied' : 'URL'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => downloadAsset(asset.url, asset.filename)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {getTotalAssetCount() === 0 && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No assets found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewAsset} onOpenChange={() => setPreviewAsset(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{previewAsset?.filename}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewAsset(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {previewAsset && (
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center max-h-[60vh] overflow-auto">
                <img
                  src={previewAsset.url}
                  alt={previewAsset.filename}
                  className="max-w-full h-auto"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => copyUrl(previewAsset.url)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button
                  onClick={() => downloadAsset(previewAsset.url, previewAsset.filename)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Type:</strong> {previewAsset.type}</p>
                <p><strong>Size:</strong> {previewAsset.size_mb} MB</p>
                <p><strong>URL:</strong> <code className="bg-gray-100 px-2 py-1 rounded text-xs">{previewAsset.url}</code></p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetManagement;
